import {
  startOfMonth,
  endOfMonth,
  subMonths,
  subDays,
  startOfQuarter,
  subQuarters,
  endOfQuarter,
  startOfYear,
  subYears,
  endOfYear,
  isWithinInterval,
} from "date-fns";
import type { Deal, Task, DealNote, Sale } from "../types";
import type { FilterOptions, PeriodFilter } from "./dashboardUtils";

export interface DealResponseTimeItem {
  dealId: string | number;
  dealName: string;
  salesId: string | null;
  createdAtMs: number;
  firstActivityMs: number | null;
  responseTimeMinutes: number | null;
  activityType?: string;
}

export interface MemberResponseTimeRank {
  salesId: string;
  name: string;
  initials: string;
  leadsReceived: number;
  leadsResponded: number;
  leadsWaiting: number;
  avgMinutes: number | null;
  avgReadable: string;
  avgMinutesFormatted: string;
}

export interface ResponseTimeSummary {
  leadsReceived: number;
  leadsResponded: number;
  leadsWaiting: number;
  avgMinutes: number | null;
  avgMinutesFormatted: string;
  avgReadable: string;
}

export interface ResponseTimeVariation {
  percentChange: number | null;
  diffValue: number | null;
  hasPreviousData: boolean;
  periodLabel: string;
}

export interface ResponseTimeDashboardData {
  current: ResponseTimeSummary;
  previous: ResponseTimeSummary | null;
  variations: {
    leadsReceived: ResponseTimeVariation;
    avgMinutes: ResponseTimeVariation;
    leadsWaiting: ResponseTimeVariation;
  };
  ranking: MemberResponseTimeRank[];
}

/**
 * Formata minutos em string legível:
 * Ex.: 47min, 2h 15min, 1d 3h
 */
export function formatReadableResponseTime(
  minutes: number | null | undefined,
): string {
  if (minutes === null || minutes === undefined || isNaN(minutes)) {
    return "—";
  }

  if (minutes < 1) {
    const seconds = Math.max(1, Math.round(minutes * 60));
    return `${seconds}s`;
  }

  const totalMinutes = Math.round(minutes);

  if (totalMinutes < 60) {
    return `${totalMinutes}min`;
  }

  const totalHours = Math.floor(totalMinutes / 60);
  const remMinutes = totalMinutes % 60;

  if (totalHours < 24) {
    return remMinutes > 0
      ? `${totalHours}h ${remMinutes}min`
      : `${totalHours}h`;
  }

  const days = Math.floor(totalHours / 24);
  const remHours = totalHours % 24;

  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
}

/**
 * Formata a média em minutos com 1 casa decimal no padrão pt-BR (ex.: "47,3 min")
 */
export function formatMinutesDecimal(
  minutes: number | null | undefined,
): string {
  if (minutes === null || minutes === undefined || isNaN(minutes)) {
    return "—";
  }
  return `${minutes.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} min`;
}

/**
 * Retorna os intervalos de data do período atual e do período anterior equivalente
 */
export function getPeriodIntervals(period: PeriodFilter): {
  current: { start: Date; end: Date } | null;
  previous: { start: Date; end: Date } | null;
  label: string;
} {
  const now = new Date();

  switch (period) {
    case "this_month": {
      const currentStart = startOfMonth(now);
      const currentEnd = endOfMonth(now);
      const prevMonthDate = subMonths(now, 1);
      const previousStart = startOfMonth(prevMonthDate);
      const previousEnd = endOfMonth(prevMonthDate);
      return {
        current: { start: currentStart, end: currentEnd },
        previous: { start: previousStart, end: previousEnd },
        label: "vs. mês anterior",
      };
    }
    case "last_30_days": {
      const currentStart = subDays(now, 30);
      const previousStart = subDays(now, 60);
      const previousEnd = subDays(now, 30);
      return {
        current: { start: currentStart, end: now },
        previous: { start: previousStart, end: previousEnd },
        label: "vs. 30 dias anteriores",
      };
    }
    case "this_quarter": {
      const currentStart = startOfQuarter(now);
      const prevQuarterDate = subQuarters(now, 1);
      const previousStart = startOfQuarter(prevQuarterDate);
      const previousEnd = endOfQuarter(prevQuarterDate);
      return {
        current: { start: currentStart, end: now },
        previous: { start: previousStart, end: previousEnd },
        label: "vs. trimestre anterior",
      };
    }
    case "this_year": {
      const currentStart = startOfYear(now);
      const prevYearDate = subYears(now, 1);
      const previousStart = startOfYear(prevYearDate);
      const previousEnd = endOfYear(prevYearDate);
      return {
        current: { start: currentStart, end: now },
        previous: { start: previousStart, end: previousEnd },
        label: "vs. ano anterior",
      };
    }
    case "all":
    default:
      return {
        current: null,
        previous: null,
        label: "Todo o período",
      };
  }
}

/**
 * Extrai candidatos de data/hora (em ms) para uma tarefa vinculada ao negócio
 */
function extractTaskCandidateTimestamps(
  task: Task,
  dealCreatedMs: number,
): number[] {
  const rawDates = [
    task.created_at,
    task.done_date,
    task.data_inicio,
    task.due_date,
  ].filter(Boolean) as string[];

  const validTimestamps: number[] = [];
  // Tolerância de 60s para pequenas diferenças de relógio entre criação do lead e 1ª atividade imediata
  const toleranceMs = 60 * 1000;

  for (const dateStr of rawDates) {
    const ms = new Date(dateStr).getTime();
    if (!isNaN(ms) && ms >= dealCreatedMs - toleranceMs) {
      validTimestamps.push(Math.max(dealCreatedMs, ms));
    }
  }

  return validTimestamps;
}

/**
 * Calcula o Response Time de uma lista de negócios com base em qualquer atividade
 * executada/registrada pelo proprietário atual do negócio.
 */
export function computeDealsResponseTimeItems(
  deals: Deal[],
  tasks: Task[],
  dealNotes: DealNote[],
): DealResponseTimeItem[] {
  // Indexa tarefas por deal_id e por contact_id para busca O(1)
  const tasksByDealId = new Map<string, Task[]>();
  const tasksByContactId = new Map<string, Task[]>();

  for (const t of tasks) {
    if (t.deal_id != null) {
      const key = String(t.deal_id);
      const list = tasksByDealId.get(key) || [];
      list.push(t);
      tasksByDealId.set(key, list);
    } else if (t.contact_id != null) {
      const cKey = String(t.contact_id);
      const list = tasksByContactId.get(cKey) || [];
      list.push(t);
      tasksByContactId.set(cKey, list);
    }
  }

  // Indexa notas por deal_id
  const notesByDealId = new Map<string, DealNote[]>();
  for (const n of dealNotes) {
    if (n.deal_id != null) {
      const key = String(n.deal_id);
      const list = notesByDealId.get(key) || [];
      list.push(n);
      notesByDealId.set(key, list);
    }
  }

  const toleranceMs = 60 * 1000;

  return deals.map((deal) => {
    const dealIdStr = String(deal.id);
    const ownerSalesId: string | null =
      deal.sales_id != null && String(deal.sales_id) !== ""
        ? String(deal.sales_id)
        : null;
    const createdAtMs = deal.created_at
      ? new Date(deal.created_at).getTime()
      : NaN;

    if (!ownerSalesId || isNaN(createdAtMs)) {
      return {
        dealId: deal.id,
        dealName: deal.name,
        salesId: ownerSalesId,
        createdAtMs: isNaN(createdAtMs) ? 0 : createdAtMs,
        firstActivityMs: null,
        responseTimeMinutes: null,
      };
    }

    let earliestMs: number | null = null;
    let earliestType: string | undefined = undefined;

    const considerTimestamp = (ms: number, typeLabel?: string) => {
      if (isNaN(ms) || ms < createdAtMs - toleranceMs) return;
      const clampedMs = Math.max(createdAtMs, ms);
      if (earliestMs === null || clampedMs < earliestMs) {
        earliestMs = clampedMs;
        earliestType = typeLabel;
      }
    };

    // 1. Qualquer atividade na tabela tasks (vinculada por deal_id ou contact_ids) feita pelo proprietário atual
    const directTasks = tasksByDealId.get(dealIdStr) || [];
    const contactTasks: Task[] = [];
    if (Array.isArray(deal.contact_ids)) {
      for (const cid of deal.contact_ids) {
        const found = tasksByContactId.get(String(cid));
        if (found) contactTasks.push(...found);
      }
    }

    const allDealTasks = [...directTasks, ...contactTasks];
    for (const task of allDealTasks) {
      // Só conta atividade feita pelo proprietário atual do negócio
      const taskOwner: string =
        task.sales_id != null && String(task.sales_id) !== ""
          ? String(task.sales_id)
          : ownerSalesId;

      if (taskOwner !== ownerSalesId) continue;

      const candidateTimestamps = extractTaskCandidateTimestamps(
        task,
        createdAtMs,
      );
      for (const ms of candidateTimestamps) {
        considerTimestamp(ms, task.type || "atividade");
      }
    }

    // 2. Anotações do negócio (deal_notes) feitas pelo proprietário atual
    const dealNotesList = notesByDealId.get(dealIdStr) || [];
    for (const note of dealNotesList) {
      const noteOwner: string =
        note.sales_id != null && String(note.sales_id) !== ""
          ? String(note.sales_id)
          : ownerSalesId;
      if (noteOwner !== ownerSalesId) continue;

      if (note.date) {
        considerTimestamp(new Date(note.date).getTime(), "nota");
      }
    }

    // 3. Eventos de atividade no histórico embutido do negócio (deal.history)
    if (Array.isArray(deal.history)) {
      for (const ev of deal.history) {
        if (
          ev.type === "activity_created" ||
          ev.type === "activity_completed" ||
          ev.type === "note_added"
        ) {
          const evOwner: string =
            ev.sales_id != null && String(ev.sales_id) !== ""
              ? String(ev.sales_id)
              : ownerSalesId;
          if (evOwner !== ownerSalesId) continue;

          const rawDate = ev.activity_created_at || ev.created_at;
          if (rawDate) {
            considerTimestamp(
              new Date(rawDate).getTime(),
              ev.activity_type || ev.type,
            );
          }
        }
      }
    }

    // 4. Histórico de chamadas VoIP em custom_fields.calls_history
    const callsHistory = deal.custom_fields?.calls_history;
    if (Array.isArray(callsHistory)) {
      for (const call of callsHistory) {
        if (!call?.created_at) continue;
        const callOwner: string =
          call.sales_id != null && String(call.sales_id) !== ""
            ? String(call.sales_id)
            : ownerSalesId;
        if (callOwner !== ownerSalesId) continue;

        considerTimestamp(new Date(call.created_at).getTime(), "chamada");
      }
    }

    const responseTimeMinutes =
      earliestMs !== null ? (earliestMs - createdAtMs) / (1000 * 60) : null;

    return {
      dealId: deal.id,
      dealName: deal.name,
      salesId: ownerSalesId,
      createdAtMs,
      firstActivityMs: earliestMs,
      responseTimeMinutes,
      activityType: earliestType,
    };
  });
}

function summarizeItems(items: DealResponseTimeItem[]): ResponseTimeSummary {
  const leadsReceived = items.length;
  const respondedItems = items.filter(
    (item) =>
      item.responseTimeMinutes !== null && !isNaN(item.responseTimeMinutes),
  );
  const leadsResponded = respondedItems.length;
  const leadsWaiting = Math.max(0, leadsReceived - leadsResponded);

  const avgMinutes =
    leadsResponded > 0
      ? respondedItems.reduce(
          (acc, item) => acc + (item.responseTimeMinutes || 0),
          0,
        ) / leadsResponded
      : null;

  return {
    leadsReceived,
    leadsResponded,
    leadsWaiting,
    avgMinutes,
    avgMinutesFormatted: formatMinutesDecimal(avgMinutes),
    avgReadable: formatReadableResponseTime(avgMinutes),
  };
}

function computeVariation(
  currentVal: number | null,
  previousVal: number | null,
  periodLabel: string,
): ResponseTimeVariation {
  if (
    currentVal === null ||
    previousVal === null ||
    isNaN(currentVal) ||
    isNaN(previousVal)
  ) {
    return {
      percentChange: null,
      diffValue: null,
      hasPreviousData: false,
      periodLabel,
    };
  }

  const diffValue = currentVal - previousVal;
  if (previousVal === 0) {
    return {
      percentChange: currentVal === 0 ? 0 : 100,
      diffValue,
      hasPreviousData: true,
      periodLabel,
    };
  }

  const percentChange = ((currentVal - previousVal) / previousVal) * 100;
  return {
    percentChange,
    diffValue,
    hasPreviousData: true,
    periodLabel,
  };
}

/**
 * Filtra negócios apenas por funil e vendedor (sem filtro de data)
 */
function filterDealsByPipelineAndOwner(
  deals: Deal[],
  filters: FilterOptions,
): Deal[] {
  return deals.filter((deal) => {
    if (filters.pipelineId !== "all") {
      const dealFunil = String(deal.funil_id || "")
        .toLowerCase()
        .trim();
      const targetFunil = filters.pipelineId.toLowerCase().trim();
      if (dealFunil !== targetFunil) {
        return false;
      }
    }

    if (filters.salesId !== "all") {
      if (String(deal.sales_id) !== String(filters.salesId)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Calcula todos os indicadores do painel Response Time (Resumo, Variações e Ranking do Time)
 */
export function calculateResponseTimeDashboardData(
  allDeals: Deal[],
  tasks: Task[],
  dealNotes: DealNote[],
  salesTeam: Sale[],
  filters: FilterOptions,
): ResponseTimeDashboardData {
  const baseDeals = filterDealsByPipelineAndOwner(allDeals, filters);
  const {
    current: currentInterval,
    previous: previousInterval,
    label: periodLabel,
  } = getPeriodIntervals(filters.period);

  const currentDeals = currentInterval
    ? baseDeals.filter((d) => {
        if (!d.created_at) return false;
        try {
          return isWithinInterval(new Date(d.created_at), currentInterval);
        } catch {
          return false;
        }
      })
    : baseDeals;

  const previousDeals = previousInterval
    ? baseDeals.filter((d) => {
        if (!d.created_at) return false;
        try {
          return isWithinInterval(new Date(d.created_at), previousInterval);
        } catch {
          return false;
        }
      })
    : [];

  const currentItems = computeDealsResponseTimeItems(
    currentDeals,
    tasks,
    dealNotes,
  );
  const currentSummary = summarizeItems(currentItems);

  const previousSummary = previousInterval
    ? summarizeItems(
        computeDealsResponseTimeItems(previousDeals, tasks, dealNotes),
      )
    : null;

  // Ranking por membro da equipe comercial
  const salesMap = new Map<
    string,
    {
      name: string;
      initials: string;
      items: DealResponseTimeItem[];
    }
  >();

  for (const s of salesTeam) {
    if (s.disabled) continue;
    const id = String(s.id);
    const name =
      `${s.first_name || ""} ${s.last_name || ""}`.trim() || `Vendedor #${id}`;
    const initials =
      ((s.first_name?.[0] || "") + (s.last_name?.[0] || "")).toUpperCase() ||
      "VD";
    salesMap.set(id, { name, initials, items: [] });
  }

  for (const item of currentItems) {
    if (!item.salesId) continue;
    let entry = salesMap.get(item.salesId);
    if (!entry) {
      entry = {
        name: `Vendedor #${item.salesId}`,
        initials: "VD",
        items: [],
      };
      salesMap.set(item.salesId, entry);
    }
    entry.items.push(item);
  }

  const ranking: MemberResponseTimeRank[] = Array.from(salesMap.entries())
    .map(([salesId, data]) => {
      const summary = summarizeItems(data.items);
      return {
        salesId,
        name: data.name,
        initials: data.initials,
        leadsReceived: summary.leadsReceived,
        leadsResponded: summary.leadsResponded,
        leadsWaiting: summary.leadsWaiting,
        avgMinutes: summary.avgMinutes,
        avgReadable: summary.avgReadable,
        avgMinutesFormatted: summary.avgMinutesFormatted,
      };
    })
    .filter(
      (member) =>
        member.leadsReceived > 0 || filters.salesId === member.salesId,
    )
    .sort((a, b) => {
      // Membros com response time calculado vêm primeiro, do menor (melhor) para o maior (pior)
      if (a.avgMinutes !== null && b.avgMinutes !== null) {
        if (a.avgMinutes !== b.avgMinutes) {
          return a.avgMinutes - b.avgMinutes;
        }
        return b.leadsResponded - a.leadsResponded;
      }
      if (a.avgMinutes !== null) return -1;
      if (b.avgMinutes !== null) return 1;
      return b.leadsReceived - a.leadsReceived;
    });

  return {
    current: currentSummary,
    previous: previousSummary,
    variations: {
      leadsReceived: computeVariation(
        currentSummary.leadsReceived,
        previousSummary ? previousSummary.leadsReceived : null,
        periodLabel,
      ),
      avgMinutes: computeVariation(
        currentSummary.avgMinutes,
        previousSummary ? previousSummary.avgMinutes : null,
        periodLabel,
      ),
      leadsWaiting: computeVariation(
        currentSummary.leadsWaiting,
        previousSummary ? previousSummary.leadsWaiting : null,
        periodLabel,
      ),
    },
    ranking,
  };
}
